/**
 * Desktop Form Response Detail Drawer
 * 
 * Features:
 * - Fixed drawer on the right side
 * - Grouped field sections
 * - Metadata display
 * - Responsive scrolling
 * - Close button and backdrop
 */

import React from 'react';
import { X, Copy, Check } from 'lucide-react';
import FormResponseField from './FormResponseField';
import {
    groupResponseFieldsBySection,
    extractMetadata,
    extractContactInfo,
    formatSubmissionTime,
    formatDateDDMMMYYYY,
} from '../utils/formResponseFormatters';

/**
 * Format submitted date safely from various input types
 * @param {Date|string|object} timestamp
 * @returns {string}
 */
const formatSubmittedDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
        const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        return formatDateDDMMMYYYY(date);
    } catch {
        return 'Unknown';
    }
};

const DesktopFormResponseDetailDrawer = ({
    response = null,
    isOpen = false,
    onClose = () => { },
    fieldSchema = null,
    variant = 'drawer', // 'drawer' | 'modal' | 'page'
}) => {
    const [copiedField, setCopiedField] = React.useState(null);

    if (!response || !isOpen) return null;

    // Guard against missing responses object
    if (!response.responses || typeof response.responses !== 'object') {
        return (
            <>
                <div
                    className={`fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-40 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={onClose}
                />
                <div className={`fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl z-50 flex flex-col border-l border-notion-border transition-transform transform ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="px-6 py-4 border-b border-notion-border bg-stone-50 flex-shrink-0 flex justify-between items-start">
                        <h3 className="font-semibold text-notion-text">Form Response</h3>
                        <button onClick={onClose} className="p-1 hover:bg-stone-200 rounded">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="px-6 py-4 text-center text-stone-600">
                        <p>No form data available</p>
                    </div>
                </div>
            </>
        );
    }

    // Group fields by section
    const groupedFields = groupResponseFieldsBySection(response.responses, fieldSchema);
    const metadata = extractMetadata(response);
    const contactInfo = extractContactInfo(response);

    // Handle copy to clipboard
    const handleCopyField = (field, value) => {
        navigator.clipboard.writeText(value || '');
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    if (variant === 'drawer') {
        return (
            <>
                {/* Backdrop */}
                <div
                    className={`fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-40 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                    onClick={onClose}
                />

                {/* Drawer */}
                <div className={`fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl z-50 flex flex-col border-l border-notion-border transition-transform transform ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}>
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-notion-border bg-stone-50 flex-shrink-0 flex justify-between items-start">
                        <div>
                            <h2 className="text-lg font-bold text-notion-text">{contactInfo.name}</h2>
                            <p className="text-xs text-stone-500 mt-1">
                                Submitted {formatSubmissionTime(response.createdAt)}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-stone-400 hover:text-stone-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-8">
                        {/* Contact Information Section */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-stone-600 uppercase tracking-wider">Quick Contact</h3>
                            <div className="bg-stone-50 rounded-lg p-4 space-y-2 text-sm">
                                {contactInfo.email && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-stone-600">{contactInfo.email}</span>
                                        <button
                                            onClick={() => handleCopyField('email', contactInfo.email)}
                                            className="text-stone-400 hover:text-stone-600 transition-colors"
                                            title="Copy email"
                                        >
                                            {copiedField === 'email' ? (
                                                <Check className="w-4 h-4 text-green-600" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                )}
                                {contactInfo.phone && (
                                    <div className="flex justify-between items-center">
                                        <a href={`tel:${contactInfo.phone}`} className="text-blue-600 hover:underline">
                                            {contactInfo.phone}
                                        </a>
                                        <button
                                            onClick={() => handleCopyField('phone', contactInfo.phone)}
                                            className="text-stone-400 hover:text-stone-600 transition-colors"
                                            title="Copy phone"
                                        >
                                            {copiedField === 'phone' ? (
                                                <Check className="w-4 h-4 text-green-600" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Response Fields Grouped by Section */}
                        {Object.entries(groupedFields).map(([section, fields]) => (
                            <div key={section} className="space-y-4">
                                <h3 className="text-xs font-bold text-stone-600 uppercase tracking-wider">
                                    {section}
                                </h3>
                                <div className="space-y-4">
                                    {fields.map((field) => (
                                        <FormResponseField
                                            key={field.id}
                                            id={field.id}
                                            label={field.label}
                                            value={field.value}
                                            type={field.type}
                                            isMetadata={false}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Metadata Section */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-stone-600 uppercase tracking-wider">Submission Info</h3>
                            <div className="space-y-4">
                                <FormResponseField
                                    id="submittedAt"
                                    label="Submitted At"
                                    value={formatSubmittedDate(metadata.submittedAt)}
                                    type="text"
                                    isMetadata={true}
                                />
                                {metadata.pageUrl && (
                                    <FormResponseField
                                        id="pageUrl"
                                        label="Submission Page"
                                        value={metadata.pageUrl}
                                        type="url"
                                        isMetadata={true}
                                    />
                                )}
                                {metadata.referrer && (
                                    <FormResponseField
                                        id="referrer"
                                        label="Referrer"
                                        value={metadata.referrer}
                                        type="url"
                                        isMetadata={true}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-notion-border bg-stone-50 flex-shrink-0">
                        <button
                            onClick={onClose}
                            className="w-full px-4 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </>
        );
    }

    // Modal or Page variants can be implemented similarly
    return null;
};

export default DesktopFormResponseDetailDrawer;
